import StarIcon from '@/components/elements/redesign/StarIcon/StarIcon';
import { classnames, roundRating } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED } from '@/constants/reviewConstants';

import { _t } from '@/locale';
import styles from './Stats.module.scss';

const getStars = (rating, ratingFilter, size) => {
  let empty = false;
  return [1, 2, 3, 4, 5].map((i, key) => {
    let fill = 100;
    if (i > rating) {
      fill = !empty ? (rating % 1) * 100 : 0;
      empty = true;
    }
    return (
      <div key={`${i + 1}-${key}`} className="relative">
        <StarIcon className="bg-black-100" style={{ width: size, height: size }} />
        <StarIcon
          className={classnames(
            'absolute inset-0',
            ratingFilter === 0 || ratingFilter === rating ? 'bg-warning-500' : 'bg-black-200',
          )}
          style={{ width: size, height: size, clipPath: `inset(0 ${100 - fill}% 0 0)` }}
        />
      </div>
    );
  });
};

const ReviewStats = (props) => {
  const {
    stats: { score, count },
    ratingCounts,
    reviewCounts,
    ratingFilter = 0,
    onRatingClick,
    onTitleClick,
    withRatingLabel,
    showScore = true,
    withEmptyState,
  } = props;
  const { isMobileView } = useMobileView();
  if ((!score || !count || !ratingCounts || count < 1) && !withEmptyState) {
    return null;
  }

  const withEnoughReviews = score && count >= MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED;

  const handleRatingClick = (rating) => {
    onRatingClick(rating);
  };

  const starLines = Object.keys(ratingCounts)
    .sort((a, b) => b - a)
    .map((rating, i) => (
      <div
        key={`${i + 1}-${rating}`}
        className={classnames('p-3xs flex items-center justify-end rounded-sm border-4', {
          [styles.active]: ratingFilter === rating || ratingFilter === 0,
          [styles.disabled]: ratingFilter !== rating && ratingFilter !== 0,
          'hover:border-border_focus': withEnoughReviews,
          'cursor-pointer': !!handleRatingClick && withEnoughReviews,
        })}
        role={withEnoughReviews ? 'button' : 'presentation'}
        onClick={
          withEnoughReviews
            ? () => {
                handleRatingClick(rating);
              }
            : undefined
        }>
        <span className="text-fg_secondary pr-sm">{rating}</span>
        <div className="gap-xxs flex">{getStars(rating, ratingFilter, 16)}</div>
        <div className={classnames(styles.pgbar, 'mx-3 h-[4px] flex-1 overflow-hidden rounded-sm md:min-w-[200px]')}>
          <div
            className={classnames(
              styles.pgbarval,
              'h-[4px] transform rounded-l-sm transition-all duration-300 ease-in-out',
            )}
            role={'progressbar'}
            style={{ width: (ratingCounts[rating] / count) * 100 + '%' }}
            aria-valuemin="0"
            aria-valuemax="100"></div>
        </div>
        <span
          className={classnames('text-m w-[30px] text-right  md:w-[50px]', {
            'text-fg_link text-link underline underline-offset-4': withEnoughReviews,
          })}>
          ({ratingCounts[rating] < 1000 ? ratingCounts[rating] : '+999'})
        </span>
      </div>
    ));

  const getHeading = () => {
    if (withEnoughReviews) {
      return (
        <h4
          className={classnames('gap-sm flex items-end', {
            'hover:text-fg_link': !!onTitleClick,
          })}
          role={!!onTitleClick ? 'button' : 'presentation'}
          onClick={
            !!onTitleClick
              ? () => {
                  onTitleClick();
                }
              : undefined
          }>
          <span className="text-h-3xl font-bold">{roundRating(score, 1)}</span>
          <span className="text-h-l pb-xxs">{_t('of')}</span>
          <span className="text-h-3xl font-bold">{'5'}</span>
        </h4>
      );
    }

    return (
      <div className="mt-0">
        <p className="text-fg_secondary text-l">{_t('notEnoughReviewsDescription')}</p>
      </div>
    );
  };

  return (
    <div
      className={classnames('gap-3 py-6', {
        [props.className]: !!props.className,
      })}>
      {showScore && (
        <div className="flex items-center justify-between pl-2">
          {getHeading()}
          <div>
            {!ratingFilter && !isMobileView ? (
              <div className="gap-xxs flex">{getStars(roundRating(score, 1), ratingFilter, 24)}</div>
            ) : null}
            {withRatingLabel && !ratingFilter && !isMobileView && withEnoughReviews ? (
              <p className="text-fg_secondary text-s mt-1">
                {_t('BasedOnNumberOfReviews', { count: props.stats.count })}
              </p>
            ) : null}
          </div>
        </div>
      )}
      {withEnoughReviews ? <div className="flex w-full flex-1 flex-col">{starLines}</div> : null}
      {Boolean(ratingFilter) && (
        <div className="mt-6 flex flex-col gap-1">
          <span className="font-semibold text-black">{_t('ReviewsWithStarRating', { rating: ratingFilter })}</span>
          <span className="text-black-600 text-m">
            {_t('reviewsFromRatingsCounts', {
              reviews: reviewCounts[ratingFilter],
              ratings: ratingCounts[ratingFilter],
            })}
          </span>
        </div>
      )}
    </div>
  );
};
export default ReviewStats;
